'use client'

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { formatSyncKey } from '../_lib/progress'
import { progress, useSyncInfo } from '../_lib/progress-client'
import { useGdColors } from '../_lib/theme-client'
import { IconClose, IconCopy, IconEye } from './icons'

function timeAgo(ms: number | null): string {
  if (!ms) return ''
  const s = Math.round((Date.now() - ms) / 1000)
  if (s < 10) return 'just now'
  if (s < 60) return `${s}s ago`
  const m = Math.round(s / 60)
  if (m < 60) return `${m} min ago`
  return `${Math.round(m / 60)} h ago`
}

const STATUS_TEXT = {
  local: 'Saved on this device only',
  syncing: 'Syncing…',
  synced: 'Synced',
  offline: 'Offline — will sync when you’re back',
  error: 'Sync hiccup — retrying',
} as const

function QrCode({ text }: { text: string }) {
  const ref = useRef<HTMLCanvasElement>(null)
  const colors = useGdColors()
  useEffect(() => {
    let cancelled = false
    void import('qrcode').then(QR => {
      if (cancelled || !ref.current) return
      void QR.toCanvas(ref.current, text, {
        margin: 1,
        width: 168,
        color: { dark: colors.ink, light: colors.surface },
        errorCorrectionLevel: 'M',
      })
    })
    return () => {
      cancelled = true
    }
  }, [text, colors])
  return <canvas ref={ref} width={168} height={168} className="rounded-[10px]" aria-label="QR code for your sync link" />
}

export default function SyncPanel({ onClose }: { onClose: () => void }) {
  const sync = useSyncInfo()
  const [reveal, setReveal] = useState(false)
  const [copied, setCopied] = useState<string | null>(null)
  const [input, setInput] = useState('')
  const [linking, setLinking] = useState(false)
  const [linkMsg, setLinkMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const [confirm, setConfirm] = useState<null | 'reset' | 'delete'>(null)
  const closeRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    closeRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [onClose])

  const key = sync.key
  const link = key ? `${window.location.origin}/sync#${key}` : ''

  async function copy(text: string, what: string) {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(what)
      setTimeout(() => setCopied(null), 1600)
    } catch {
      setCopied(null)
    }
  }

  async function link_() {
    setLinking(true)
    setLinkMsg(null)
    const res = await progress.linkKey(input)
    setLinking(false)
    if (res.ok) {
      setInput('')
      setLinkMsg({ ok: true, text: 'Linked! Progress from both devices has been merged.' })
    } else {
      setLinkMsg({ ok: false, text: res.error })
    }
  }

  const root = document.querySelector('.gd-root') ?? document.body

  return createPortal(
    <div className="gd-overlay" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <aside className="gd-drawer" role="dialog" aria-modal="true" aria-labelledby="gd-sync-title">
        <div className="flex items-center justify-between border-b px-6 py-4" style={{ borderColor: 'var(--gd-rule)' }}>
          <p className="gd-label">Progress sync</p>
          <button ref={closeRef} type="button" className="gd-icon-btn" onClick={onClose} aria-label="Close">
            <IconClose />
          </button>
        </div>

        <div className="space-y-8 px-6 py-7">
          <section>
            <h2 id="gd-sync-title" className="gd-serif text-[34px] leading-[1.05]">
              Learn anywhere. <em className="gd-accent-italic">No account.</em>
            </h2>
            <p className="mt-3 text-[15.5px] leading-relaxed" style={{ color: 'var(--gd-ink-2)' }}>
              Your progress saves on this device automatically. To pick up on another device, it gets a{' '}
              <strong style={{ color: 'var(--gd-ink)' }}>sync key</strong> — no email, no password, nothing about you. Both
              devices merge their progress, so you can even learn offline on both.
            </p>
            <div className="mt-4 flex items-center gap-2 text-[14px]" style={{ color: 'var(--gd-ink-2)' }}>
              <span className="gd-status-dot" data-status={key ? sync.status : 'local'} />
              <span>
                {key ? STATUS_TEXT[sync.status] : STATUS_TEXT.local}
                {key && sync.status === 'synced' && sync.lastSyncedAt ? ` · ${timeAgo(sync.lastSyncedAt)}` : ''}
              </span>
            </div>
          </section>

          <section>
            <p className="gd-label mb-3">This device’s key</p>
            {key ? (
              <>
                <div className="gd-keybox">
                  <span className="select-all">{reveal ? formatSyncKey(key) : `${formatSyncKey(key).slice(0, 5)}-•••••-•••••-•••••`}</span>
                  <span className="flex gap-1">
                    <button type="button" className="gd-icon-btn !h-8 !w-8" onClick={() => setReveal(r => !r)} aria-label={reveal ? 'Hide key' : 'Show key'}>
                      <IconEye size={16} />
                    </button>
                    <button type="button" className="gd-icon-btn !h-8 !w-8" onClick={() => copy(formatSyncKey(key), 'key')} aria-label="Copy key">
                      <IconCopy size={16} />
                    </button>
                  </span>
                </div>
                <div className="mt-4 flex items-start gap-4">
                  <div className="shrink-0 rounded-[12px] border p-1.5" style={{ borderColor: 'var(--gd-rule)', background: 'var(--gd-surface)' }}>
                    <QrCode text={link} />
                  </div>
                  <div className="space-y-2 pt-1 text-[14px] leading-relaxed" style={{ color: 'var(--gd-ink-2)' }}>
                    <p>Scan with your phone, or open the link on any device to link it.</p>
                    <button type="button" className="gd-btn gd-btn-ghost gd-btn-sm" onClick={() => copy(link, 'link')}>
                      <IconCopy size={15} /> {copied === 'link' ? 'Copied!' : 'Copy sync link'}
                    </button>
                    {copied === 'key' && <p style={{ color: 'var(--gd-green)' }}>Key copied.</p>}
                  </div>
                </div>
                <p className="mt-4 text-[13px] leading-relaxed" style={{ color: 'var(--gd-ink-3)' }}>
                  Treat the key like a password: anyone holding it can see and add to this progress (lesson completion and
                  code drafts — nothing else is stored).
                </p>
              </>
            ) : (
              <div className="space-y-3">
                <p className="text-[15px] leading-relaxed" style={{ color: 'var(--gd-ink-2)' }}>
                  A key is created automatically once you finish something. Want one now?
                </p>
                <button type="button" className="gd-btn gd-btn-ink gd-btn-sm" onClick={() => progress.ensureKey()}>
                  Create my sync key
                </button>
              </div>
            )}
          </section>

          <section>
            <p className="gd-label mb-3">Got a key from another device?</p>
            <form
              className="flex gap-2"
              onSubmit={e => {
                e.preventDefault()
                void link_()
              }}
            >
              <input
                className="gd-input min-w-0 flex-1 uppercase"
                placeholder="XXXXX-XXXXX-XXXXX-XXXXX"
                value={input}
                onChange={e => setInput(e.target.value)}
                aria-label="Sync key from another device"
                autoComplete="off"
                spellCheck={false}
              />
              <button type="submit" className="gd-btn gd-btn-primary gd-btn-sm !h-[42px]" disabled={linking || input.trim().length < 20}>
                {linking ? 'Linking…' : 'Link'}
              </button>
            </form>
            {linkMsg && (
              <p className="mt-2 text-[14px]" style={{ color: linkMsg.ok ? 'var(--gd-green)' : 'var(--gd-pink)' }}>
                {linkMsg.text}
              </p>
            )}
          </section>

          <section className="border-t pt-6" style={{ borderColor: 'var(--gd-rule)' }}>
            <p className="gd-label mb-3">Housekeeping</p>
            {confirm === null && (
              <div className="flex flex-wrap gap-2">
                <button type="button" className="gd-btn gd-btn-ghost gd-btn-xs" onClick={() => setConfirm('reset')}>
                  Start over from zero
                </button>
                {key && (
                  <button type="button" className="gd-btn gd-btn-ghost gd-btn-xs" onClick={() => setConfirm('delete')}>
                    Delete synced copy
                  </button>
                )}
              </div>
            )}
            {confirm === 'reset' && (
              <div className="space-y-2 text-[14px]" style={{ color: 'var(--gd-ink-2)' }}>
                <p>This clears every lesson, answer and code draft — on every device using this key.</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="gd-btn gd-btn-primary gd-btn-xs"
                    onClick={() => {
                      progress.resetAll()
                      setConfirm(null)
                    }}
                  >
                    Yes, reset everything
                  </button>
                  <button type="button" className="gd-btn gd-btn-ghost gd-btn-xs" onClick={() => setConfirm(null)}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
            {confirm === 'delete' && (
              <div className="space-y-2 text-[14px]" style={{ color: 'var(--gd-ink-2)' }}>
                <p>Removes the copy on our server and stops syncing. Progress stays on this device.</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="gd-btn gd-btn-primary gd-btn-xs"
                    onClick={async () => {
                      await progress.deleteRemote()
                      setConfirm(null)
                    }}
                  >
                    Delete it
                  </button>
                  <button type="button" className="gd-btn gd-btn-ghost gd-btn-xs" onClick={() => setConfirm(null)}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      </aside>
    </div>,
    root,
  )
}
